import { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import { GET_ALL_AUDIENCE_TARGETING_METRICS } from '@/graphql/audience-targeting-metrics-queries'

/**
 * Custom hook for managing audience targeting metrics fetching
 * Handles smart fetching logic to prevent duplicate API calls
 */
export const useAudienceTargetingMetrics = ({ campaignId, adAccountId, dateRange, currency }) => {
    // Use unified query to fetch all metrics at once
    const {
        data: allMetricsData,
        loading: metricsLoading,
        error: fetchError,
        refetch,
    } = useQuery(GET_ALL_AUDIENCE_TARGETING_METRICS, {
        variables: {
            campaignId: campaignId || '',
            adAccountId: adAccountId || '',
            dateFrom: dateRange?.from ? dateRange.from.toISOString().split('T')[0] : undefined,
            dateTo: dateRange?.to ? dateRange.to.toISOString().split('T')[0] : undefined,
        },
        skip: !campaignId || !adAccountId || !dateRange?.from || !dateRange?.to,
        fetchPolicy: 'network-only',
    })

    // Process and organize metrics data by widget
    const processedMetricsData = useMemo(() => {
        if (!allMetricsData) return null

        return {
            genderMetrics: allMetricsData.genderMetrics || [],
            ageMetrics: allMetricsData.ageMetrics || [],
            deviceMetrics: allMetricsData.deviceMetrics || [],
            platformMetrics: allMetricsData.platformMetrics || [],
            placementMetrics: allMetricsData.placementMetrics || [],
            countryMetrics: allMetricsData.countryMetrics || [],
        }
    }, [allMetricsData])

    // Simple refetch function for child components
    const refetchMetrics = () => refetch()
    // const refetchMetrics = useCallback(() => {
    // return refetch()
    // }, [refetch])

    // Helper function to get default date range (last 30 days)
    const getDefaultDateRange = useCallback(() => {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - 30)
        return {
            from: startDate,
            to: endDate,
        }
    }, [])

    const formatMetricValue = useCallback((value, column, replaceNullsWithDash = false) => {
        const dataType = column.dataType
        if (value === null || value === undefined || (value === '' && replaceNullsWithDash)) return '-'

        switch (dataType) {
            case 'currency':
                return formatCurrencyAutoLocale({ amount: value, currency, showCode: false })
            case 'percentage':
                return `${Number(value).toFixed(2)}%`
            case 'number':
                if (column.apiKey === 'roas') return `${Number(value).toFixed(2)}`
                else return Number(value).toLocaleString()
            default:
                return String(value)
        }
    }, [])

    return {
        metricsLoading,
        refetchMetrics,
        getDefaultDateRange,
        formatMetricValue,
        fetchError,
        // Processed metrics data for each widget
        allMetricsData: processedMetricsData,
    }
}
